/**
 * Shell Component
 * Phase 2: Product Shell & Navigation
 * 
 * Main application shell with persistent sidebar, topbar, and content area.
 * Replaces Phase 1 simple header with full product navigation structure.
 */

import { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { HmvmFooter, HmvmHeader } from './HmvmChrome'
import { Breadcrumbs } from './Breadcrumbs'
import { CommandPalette, useCommandPalette } from './CommandPalette'
import { useAppContext } from '../contexts/AppContext'
import { getBreadcrumbs } from '../lib/navigation'

interface ShellProps {
  children: ReactNode
}

function Shell({ children }: ShellProps) {
  const location = useLocation()
  const { sidebarCollapsed, toggleSidebar, currentProject, currentAudit } = useAppContext()
  const commandPalette = useCommandPalette()

  // Get breadcrumbs for current route
  const breadcrumbs = getBreadcrumbs(location.pathname)

  return (
    <div
      className="shell"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'var(--reviewer-bg-primary)',
      }}
    >
      <HmvmHeader />

      <div
        className="shell-body"
        style={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
        }}
      >
      {/* Sidebar */}
      <Sidebar collapsed={sidebarCollapsed} onToggleCollapsed={toggleSidebar} />

      {/* Main Content Area */}
      <div
        className="shell-main"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // Allow flex item to shrink below content size
        }}
      >
        <Topbar
          projectName={currentProject?.name}
          auditName={currentAudit?.name}
        />

        {/* Content Wrapper */}
        <div
          className="shell-content"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          {/* Breadcrumbs & Content Header */}
          <div
            className="shell-content-header"
            style={{
              padding: '0 32px',
              borderBottom: '1px solid var(--reviewer-border)',
            }}
          >
            <Breadcrumbs trail={breadcrumbs} />
          </div>

          {/* Page Content */}
          <main
            className="shell-content-main"
            role="main"
            style={{
              flex: 1,
              padding: '24px 32px',
              overflow: 'auto',
            }}
          >
            {children}
          </main>

          {/* Footer */}
        </div>
      </div>
      </div>

      <HmvmFooter />

      {/* Command Palette (Cmd/Ctrl+K) */}
      <CommandPalette isOpen={commandPalette.isOpen} onClose={commandPalette.close} />
    </div>
  )
}

export default Shell
